"use client";

import Link from "next/link";
import Image from "next/image";
import { Input, Button, AppStoreBadges } from "@tesserix/web";
import {
  products,
  industryListPlain,
} from "@/app/(marketing)/products/[slug]/products-data";

const footerNavigation = {
  products: [
    { name: "Mark8ly", href: "/products/mark8ly" },
    { name: "Fe3dr", href: "/products/fe3dr" },
    { name: "Helivanta", href: "/products/helivanta" },
    { name: "Roamie", href: "/products/roamie" },
    { name: "Dwellm8", href: "/products/dwellm8" },
    { name: "Kora", href: "/products/kora" },
  ],
  company: [
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
    { name: "Careers", href: "/careers" },
  ],
  resources: [
    { name: "Documentation", href: "https://docs.tesserix.app", external: true },
    { name: "Design System", href: "https://ui.tesserix.app", external: true },
    { name: "Blog", href: "https://blog.tesserix.app", external: true },
    { name: "GitHub", href: "https://github.com/tesserix", external: true },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
  ],
};

/** The mono uppercase eyebrow used for every label on the marketing site. */
const EYEBROW_CLASS =
  "font-mono text-[0.7rem] font-medium uppercase tracking-[0.14em] text-muted-foreground";

interface FooterColumnProps {
  title: string;
  items: ReadonlyArray<{ name: string; href: string; external?: boolean }>;
}

const FOOTER_LINK_CLASS =
  "text-sm text-foreground/80 transition-colors hover:text-foreground";

function FooterColumn({ title, items }: FooterColumnProps) {
  return (
    <div>
      <h3 className={EYEBROW_CLASS}>{title}</h3>
      <ul role="list" className="mt-5 space-y-3">
        {items.map((item) => (
          <li key={item.name}>
            {item.external ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className={FOOTER_LINK_CLASS}
              >
                {item.name}
              </a>
            ) : (
              <Link href={item.href} className={FOOTER_LINK_CLASS}>
                {item.name}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  // Only launched products with a real app store presence get a badge here.
  // Fe3dr's vendor (home-cook) app is a second audience surfaced on the Fe3dr
  // product page, not the footer.
  const listedProducts = Object.entries(products).filter(
    ([, product]) => product.listings,
  );

  return (
    <footer className="border-t bg-muted/30" role="contentinfo">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-1 gap-x-8 gap-y-14 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Image
              src="/logo.png"
              alt="Tesserix"
              width={116}
              height={28}
              sizes="116px"
            />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-muted-foreground">
              A product studio building specialized software — one industry at
              a time. {industryListPlain()}.
            </p>
            {/* Newsletter signup */}
            <div className="mt-8">
              <p className={EYEBROW_CLASS}>Stay updated</p>
              <form className="mt-4 flex max-w-xs gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1"
                  aria-label="Email for newsletter"
                />
                <Button type="submit" size="sm">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>

          {/* Navigation */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-4 lg:col-span-7 lg:col-start-6">
            <FooterColumn title="Products" items={footerNavigation.products} />
            <FooterColumn title="Company" items={footerNavigation.company} />
            <FooterColumn
              title="Resources"
              items={footerNavigation.resources}
            />
            <FooterColumn title="Legal" items={footerNavigation.legal} />
          </div>
        </div>

        {/* App store badges — a full-width band rather than a tall stack in the
            brand column, which left the rest of the footer visually empty. */}
        {listedProducts.length > 0 && (
          <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-8 border-t pt-10 sm:grid-cols-2 lg:grid-cols-3">
            {listedProducts.map(([slug, product]) => (
              <div key={slug}>
                <p className={EYEBROW_CLASS}>Get {product.title}</p>
                <AppStoreBadges
                  className="mt-4"
                  appName={product.title}
                  listings={product.listings!}
                  placeholder="coming-soon"
                  compact
                />
              </div>
            ))}
            {/* Fe3dr's vendor (home-cook) app is a second, real Android app —
                previously only surfaced on /products/fe3dr. Three live apps
                is a real credibility signal, so it belongs in the footer too. */}
            {Object.entries(products)
              .filter(([, product]) => product.vendorListing)
              .map(([slug, product]) => (
                <div key={`${slug}-vendor`}>
                  <p className={EYEBROW_CLASS}>Get {product.title} for home cooks</p>
                  <AppStoreBadges
                    className="mt-4"
                    appName={`${product.title} for home cooks`}
                    listings={{ android: product.vendorListing! }}
                    placeholder="coming-soon"
                    compact
                  />
                </div>
              ))}
          </div>
        )}

        {/* Legal entity, copyright and socials share one hairline so the
            footer ends on a single rule instead of three stacked ones. */}
        <div className="mt-12 flex flex-col gap-6 border-t pt-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <p className="max-w-prose font-mono text-xs tracking-[0.04em] text-muted-foreground">
              Tesserix Pty Ltd &middot; ACN 694 070 865 &middot; ABN 59 694 070
              865 &middot; &copy; {new Date().getFullYear()} Tesserix. All
              rights reserved.
            </p>
          </div>
          <div className="flex shrink-0 gap-6">
            <a
              href="https://x.com/tesserix_app"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Tesserix on X"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://github.com/tesserix"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Tesserix on GitHub"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
            <a
              href="https://au.linkedin.com/company/tesserix-pty-ltd"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Tesserix on LinkedIn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
